import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import './SupportModal.css';
import { getAuthHeaders } from '../utils/auth';
import { getApiEndpoint } from '../utils/api';

const CATEGORIES = ['Bug Report', 'Feature Request', 'Billing', 'General'];

const EMPTY_FORM = { subject: '', category: 'General', message: '' };

const SupportModal = ({ isOpen, onClose, user }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setForm(EMPTY_FORM);
      setError('');
      setSuccess(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) {
      setError('Subject and message are required.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await fetch(getApiEndpoint('/api/support/ticket'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          subject: form.subject,
          category: form.category,
          message: form.message,
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to submit ticket');
      }
      setSuccess(true);
      setTimeout(onClose, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="support-modal-overlay" onClick={onClose}>
      <div className="support-modal" onClick={(e) => e.stopPropagation()}>
        <div className="support-modal-header">
          <h2>Contact Support</h2>
          <button className="support-modal-close" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>

        {success ? (
          <div className="support-success">
            <span className="support-success-icon">✅</span>
            <p>Your message was sent! We'll get back to you shortly.</p>
          </div>
        ) : (
          <form className="support-form" onSubmit={handleSubmit}>
            {error && <div className="support-error">{error}</div>}

            <div className="support-field">
              <label htmlFor="support-subject">Subject</label>
              <input
                id="support-subject"
                name="subject"
                type="text"
                value={form.subject}
                onChange={handleChange}
                placeholder="Brief description of your issue"
                disabled={loading}
              />
            </div>

            <div className="support-field">
              <label htmlFor="support-category">Category</label>
              <select
                id="support-category"
                name="category"
                value={form.category}
                onChange={handleChange}
                disabled={loading}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div className="support-field">
              <label htmlFor="support-message">Message</label>
              <textarea
                id="support-message"
                name="message"
                rows={5}
                value={form.message}
                onChange={handleChange}
                placeholder="Please describe your issue or request in detail…"
                disabled={loading}
              />
            </div>

            <button className="support-submit" type="submit" disabled={loading}>
              {loading ? 'Sending…' : 'Send Message'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default SupportModal;
